import React, { useState } from 'react';
import { User as UserIcon, Mail, Phone, Calendar, MapPin, Camera, Edit2, Save, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { User } from '../types';

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
    address: user?.address || '',
    bio: user?.bio || '',
    profilePicture: user?.profilePicture || ''
  });

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecciona un archivo de imagen válido.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es muy grande. Por favor, selecciona una imagen menor a 5MB.');
      return;
    }

    try {
      // Convert image to base64 for preview and storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setProfileData(prev => ({
          ...prev,
          profilePicture: result
        }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al cargar la imagen. Inténtalo de nuevo.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate required fields
      if (!profileData.firstName.trim() || !profileData.lastName.trim()) {
        alert('El nombre y apellido son obligatorios');
        return;
      }

      if (!profileData.email.trim()) {
        alert('El email es obligatorio');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(profileData.email)) {
        alert('Por favor, introduce un email válido');
        return;
      }

      // Validate bio length
      if (profileData.bio && profileData.bio.length > 500) {
        alert('La biografía no puede exceder los 500 caracteres');
        return;
      }

      // Preparar los datos para enviar al backend
      const dataToSend: Partial<User> = {
        firstName: profileData.firstName.trim(),
        lastName: profileData.lastName.trim(),
        email: profileData.email.trim(),
        phone: profileData.phone?.trim() || undefined,
        dateOfBirth: profileData.dateOfBirth || undefined,
        address: profileData.address?.trim() || undefined,
        bio: profileData.bio?.trim() || undefined,
        profilePicture: profileData.profilePicture || undefined
      };

      await updateProfile(dataToSend);
      setIsEditing(false);
      alert('Perfil actualizado exitosamente');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      
      // Mostrar mensaje de error específico si está disponible
      let errorMessage = 'Error al actualizar el perfil. Por favor, inténtalo de nuevo.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors.map((err: any) => err.msg).join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProfilePicture = () => {
    setProfileData(prev => ({
      ...prev,
      profilePicture: ''
    }));
  };

  const handleCancel = () => {
    setProfileData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
      address: user?.address || '',
      bio: user?.bio || '',
      profilePicture: user?.profilePicture || ''
    });
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No especificado';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getRoleDisplayName = (role: string) => {
    const roleNames: { [key: string]: string } = {
      admin: 'Administrador',
      dentist: 'Dentista',
      assistant: 'Asistente',
      receptionist: 'Recepcionista',
      patient: 'Paciente'
    };
    return roleNames[role] || role;
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No se pudo cargar la información del usuario</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-dental-500 to-dental-600"></div>
        <div className="px-6 py-4">
          <div className="flex items-start space-x-6">
            {/* Profile Picture */}
            <div className="relative -mt-16">
              <div className="w-32 h-32 rounded-full bg-white p-2 shadow-lg">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-dental-100 to-dental-200 flex items-center justify-center overflow-hidden">
                  {(profileData.profilePicture || user.profilePicture) ? (
                    <img
                      src={profileData.profilePicture || user.profilePicture}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-16 h-16 text-dental-600" />
                  )}
                </div>
                {isEditing && (
                  <>
                    <input
                      type="file"
                      id="profilePicture"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <div className="absolute bottom-2 right-2 flex space-x-1">
                      <label
                        htmlFor="profilePicture"
                        className="bg-dental-600 rounded-full p-2 shadow-lg hover:shadow-xl hover:bg-dental-700 transition-all cursor-pointer"
                        title="Cambiar foto"
                      >
                        <Camera className="w-4 h-4 text-white" />
                      </label>
                      {(profileData.profilePicture || user.profilePicture) && (
                        <button
                          onClick={handleRemoveProfilePicture}
                          className="bg-red-600 rounded-full p-2 shadow-lg hover:shadow-xl hover:bg-red-700 transition-all"
                          title="Eliminar foto"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      )}
                    </div>
                  </>
                )}
                {!isEditing && (
                  <div className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-lg">
                    <Camera className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>
            </div>

            {/* Basic Info */}
            <div className="flex-1 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {user.firstName} {user.lastName}
                  </h1>
                  <p className="text-lg text-dental-600 font-medium">
                    {getRoleDisplayName(user.role)}
                  </p>
                  <p className="text-gray-600 flex items-center mt-1">
                    <Mail className="w-4 h-4 mr-2" />
                    {user.email}
                  </p>
                </div>
                
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center px-4 py-2 bg-dental-600 text-white rounded-lg hover:bg-dental-700 transition-colors"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Editar Perfil
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {isSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Información Personal</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                />
              ) : (
                <p className="text-gray-900">{user.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apellidos
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                />
              ) : (
                <p className="text-gray-900">{user.lastName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correo Electrónico
              </label>
              {isEditing ? (
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                />
              ) : (
                <p className="text-gray-900 flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-gray-500" />
                  {user.email}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+34 666 123 456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                />
              ) : (
                <p className="text-gray-900 flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-gray-500" />
                  {user.phone || 'No especificado'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Información Adicional</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Nacimiento
              </label>
              {isEditing ? (
                <input
                  type="date"
                  value={profileData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                />
              ) : (
                <p className="text-gray-900 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                  {formatDate(user.dateOfBirth || '')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección
              </label>
              {isEditing ? (
                <textarea
                  value={profileData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={3}
                  placeholder="Calle, Ciudad, Código Postal"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                />
              ) : (
                <p className="text-gray-900 flex items-start">
                  <MapPin className="w-4 h-4 mr-2 text-gray-500 mt-0.5" />
                  {user.address || 'No especificado'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rol del Sistema
              </label>
              <p className="text-gray-900">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-dental-100 text-dental-800">
                  {getRoleDisplayName(user.role)}
                </span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado de la Cuenta
              </label>
              <p className="text-gray-900">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.isActive ? 'Activa' : 'Inactiva'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bio Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {user.role === 'dentist' || user.role === 'admin' ? 'Biografía Profesional' : 'Acerca de mí'}
          </h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-dental-600 hover:text-dental-700 text-sm font-medium"
            >
              Editar
            </button>
          )}
        </div>
        
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={profileData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              rows={5}
              placeholder={
                user.role === 'dentist' || user.role === 'admin' 
                  ? "Cuéntanos sobre tu experiencia profesional, especialidades, años de experiencia..."
                  : "Cuéntanos un poco sobre ti, tus intereses, hobbies..."
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dental-500 focus:border-dental-500 resize-none"
            />
            <p className="text-xs text-gray-500">
              {profileData.bio.length}/500 caracteres
            </p>
          </div>
        ) : (
          <div className="min-h-[80px]">
            {user.bio ? (
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {user.bio}
              </p>
            ) : (
              <div className="flex items-center justify-center h-20 border-2 border-dashed border-gray-300 rounded-lg">
                <p className="text-gray-500 text-sm">
                  {user.role === 'dentist' || user.role === 'admin' 
                    ? 'Añade información sobre tu experiencia profesional'
                    : 'Añade información personal para que otros te conozcan mejor'
                  }
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;